import React, { useEffect, useState, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import "@styles/pages/Redeem/step1/RedeemSection.scss";
import { Trans, useTranslation } from "react-i18next";
import { Box, Typography, Button, LinearProgress } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { setFormStep } from "@store/ui/formStepSlice";
import { getOfferList, getOfferDetail } from "@api/redeemApi";
import { setRedeemGiftDetailData } from "@store/redeem/redeemSlice";
import ChecklistIcon from "@mui/icons-material/Checklist";
import CancelIcon from "@mui/icons-material/Cancel";
import Popup from "reactjs-popup";
import useScrollToTop from "@hooks/useScrollToTop";
import VideoPlayer from "@components/ui/VideoPlayer";

const RedeemSection = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  useScrollToTop();
  const memberInfoData = useSelector(
    (state) => state.memberInfo.memberInfoData
  );

  const isChinese = i18n.language === "hk";
  const [selectedType, setSelectedType] = useState(null);
  const [offerList, setOfferList] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [visibleCount, setVisibleCount] = useState(9);
  const [openVideoPopup, setOpenVideoPopup] = useState(false);
  const [redirectToLinkUpProgress, setRedirectToLinkUpProgress] = useState(0);
  const redirectURL = isChinese
    ? "https://www.samsung.com/hk/mypage/rewards/"
    : "https://www.samsung.com/hk_en/mypage/rewards/";
  const showLinkUpPopup = openVideoPopup && memberInfoData?.member_type === 1;
  const redirectedRef = useRef(false);
  useEffect(() => {
    if (!showLinkUpPopup) return;

    // 打開 popup 先重置
    setRedirectToLinkUpProgress(0);
    redirectedRef.current = false;

    const timer = setInterval(() => {
      setRedirectToLinkUpProgress((old) => {
        const next = Math.min(old + Math.random() * 5, 100);

        if (next === 100 && !redirectedRef.current) {
          redirectedRef.current = true;
          window.open(redirectURL, "_blank", "noopener,noreferrer");
        }
        return next;
      });
    }, 100);

    return () => {
      clearInterval(timer);
      setRedirectToLinkUpProgress(0);
      redirectedRef.current = false;
    };
  }, [showLinkUpPopup]); // ✅ 只依賴呢個


  const redeemItems = [
    {
      key: "limited_time_offers", //1
      type: 1,
      icon: "/assets/images/redeem/limited_time_offers.png",
      backgroundColor: "rgba(255, 179, 179, 0.8)",
    },
    {
      key: "", //1
      type: 2,
      icon: "/assets/images/redeem/limited_time_offers.png",
      backgroundColor: "rgba(255, 179, 179, 0.8)",
    },
    {
      key: "year_round_offers", //3
      type: 3,
      icon: "/assets/images/redeem/year_round_offers.png",
      backgroundColor: "rgba(255, 213, 128, 0.8)",
    },
    {
      key: "", //1
      type: 4,
      icon: "/assets/images/redeem/limited_time_offers.png",
      backgroundColor: "rgba(255, 179, 179, 0.8)",
    },
    {
      key: "samsung_products", //5
      type: 5,
      icon: "/assets/images/redeem/samsung_products.png",
      backgroundColor: "rgba(182, 230, 166, 0.8)",
    },
    {
      key: "partner_offers", //6
      type: 6,
      icon: "/assets/images/redeem/partner_offers.png",
      backgroundColor: "rgba(166, 227, 236, 0.8)",
    },
    {
      key: "samsung_rewards", //7
      type: 7,
      icon: "/assets/images/redeem/samsung_rewards.png",
      backgroundColor: "rgba(201, 201, 245, 0.8)",
    },
    {
      key: "e_voucher", //8
      type: 8,
      icon: "/assets/images/redeem/e_voucher.png",
      backgroundColor: "rgba(255, 184, 217, 0.8)",
    },
    {
      key: "samsung_rewards", //9
      type: 9,
      icon: "/assets/images/redeem/samsung_rewards.png",
      backgroundColor: "rgba(201, 201, 245, 0.8)",
    },
  ];

  const redeem_type_color = [
    { backgroundColor: "rgba(255, 179, 179, 0.8)" }, // #FFB3B3
    { backgroundColor: "rgba(255, 213, 128, 0.8)" }, // #FFD580
    { backgroundColor: "rgba(182, 230, 166, 0.8)" }, // #B6E6A6
    { backgroundColor: "rgba(166, 227, 236, 0.8)" }, // #A6E3EC
    { backgroundColor: "rgba(201, 201, 245, 0.8)" }, // #C9C9F5
    { backgroundColor: "rgba(255, 184, 217, 0.8)" }, // #FFB8D9
    { backgroundColor: "rgba(255, 160, 122, 0.8)" }, // #FFA07A
    { backgroundColor: "rgba(122, 124, 255, 0.8)" },
    { backgroundColor: "rgba(255, 160, 122, 0.8)" }, // #FFA07A

  ];



  useEffect(() => {
    const fetchOfferList = async () => {
      try {
        const res = await getOfferList();
        const data = res.data.Data;
        setOfferList(data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchOfferList();
  }, []);

  const availableTypes = useMemo(() => {
    return new Set(offerList.map((o) => o?.redeem_type));
  }, [offerList]);

  // 2) 只顯示在 offerList 出現過的 redeemItems
  const visibleRedeemItems = useMemo(() => {
    return redeemItems.filter((item) => availableTypes.has(item.type));
  }, [availableTypes]); // redeemItems 是常數，不用放依賴

  // 3) 如果目前選中的 type 已經不存在於可用 types，重置為全部(null)
  useEffect(() => {
    if (selectedType != null && !availableTypes.has(selectedType)) {
      setSelectedType(null);
    }
  }, [availableTypes, selectedType]);

  const filteredOffers = useMemo(() => {
    if (!selectedType) return offerList;
    return offerList.filter((offer) => offer.redeem_type === selectedType);
  }, [offerList, selectedType]);

  const visibleOffers = useMemo(() => {
    return filteredOffers.slice(0, visibleCount);
  }, [filteredOffers, visibleCount]);

  const handleSeeMore = () => {
    setVisibleCount((prev) => Math.min(prev + 9, filteredOffers.length));
  };

  useEffect(() => {
    setVisibleCount(9);
  }, [selectedType]);

  const handleGoToRedeemDetailPage = (d) => {
    dispatch(setRedeemGiftDetailData(d));

    if (d?.redeem_type === 7 || d?.redeem_type === 9) {
      if (memberInfoData?.member_type === 1) {
        // 顯示 popup
        setOpenVideoPopup(true);
      } else {
        // 直接跳去 step 3
        dispatch(setFormStep({ formKey: "redeem", step: 3 }));
      }
    } else {
      // 其他 redeem_type 照舊
      dispatch(setFormStep({ formKey: "redeem", step: 1 }));
    }
  };
  return (
    <Box className="redeem-section-container">
      <Typography className="title">
        <Trans i18nKey="redeem:title" />
      </Typography>
      {/* <Box className="filters-container">
        <Box className="icon-filter-container">
          {visibleRedeemItems.map((item) => (
            <Box
              className={`icon-container ${
                selectedType === item.type ? "activeA" : ""
              }`}
              sx={{
                borderBottom:
                  selectedType === item.type
                    ? item.backgroundColor + "6px solid "
                    : "6px solid transparent",
              }}
              key={item.key}
              onClick={() => setSelectedType(item.type)}
            >
              <img
                className={`icon ${selectedType === item.type ? "active" : ""}`}
                src={`${process.env.PUBLIC_URL}${item.icon}`}
                alt=""
              />
              <Typography
                className={`text ${
                  selectedType === item.type ? "active " : ""
                }`}
              >
                <Trans i18nKey={`redeem:${item.key}`} />
              </Typography>
            </Box>
          ))}{" "}
          <Box className="reset-filters" onClick={() => setSelectedType(null)}>
            <ChecklistIcon /> <Trans i18nKey="redeem:all" />
          </Box>
        </Box>
      </Box> */}

      <Box className="card-container">
        {visibleOffers.map((item, index) => {
          return (
            <Box
              className="card"
              sx={{ cursor: item.inhand_qty === 0 && "not-allowed !important" }}
              onClick={() =>
                item.inhand_qty === 0 ? "" : handleGoToRedeemDetailPage(item)
              }
              key={index}
            >
              <Box className="card-image-container">
                <Typography className="card-gradient"></Typography>
                <Typography
                  className="card-type"
                  sx={{
                    backgroundColor:
                      redeem_type_color[item.redeem_type - 1]?.backgroundColor,
                  }}
                >
                  <Trans
                    i18nKey={`redeem:${redeemItems[item.redeem_type - 1]?.key}`}
                  />
                </Typography>
                {item.inhand_qty === 0 && (
                  <Typography
                    className="sold-out"
                    sx={{
                      backgroundColor: "#f5f5f5",
                      border: "2px soild #4e4e4e",
                    }}
                  >
                    <Trans i18nKey="redeem:sold_out" />
                  </Typography>
                )}
                <img className="card-image" src={item.image} />
              </Box>
              <Box className="card-content">
                <Typography className="card-title">
                  {isChinese ? item.title_hk : item.title_hk_en}
                </Typography>
                <Box
                  className="card-text-button"
                  sx={{
                    justifyContent:
                      (item.redeem_type === 7 || item.redeem_type === 9) && "center !important",
                  }}
                >
                  {(item.redeem_type !== 7 && item.redeem_type !== 9)&& (
                    <Typography className="card-text">
                      <Trans i18nKey="redeem:point" />
                      {item.point_required.toLocaleString()}
                    </Typography>
                  )}
                  <Typography className="redeem-button">
                    <Trans i18nKey="redeem:redeem_button" />
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
      {visibleCount < filteredOffers.length && (
        <Box className="see-more-container">
          <Button className="loading-button" onClick={handleSeeMore}>
            <Trans i18nKey="redeem:see_more" />
          </Button>
        </Box>
      )}
      {openVideoPopup && (
        <Popup open={openVideoPopup} onClose={() => setOpenVideoPopup(false)}>
          <CancelIcon
            className="close-icon"
            onClick={() => {
              setOpenVideoPopup(false);
            }}
          />
          <Box className="notice-popup-container">
            <Box className="notice-icon-container">
              <img
                className="notice-logo"
                src={`${process.env.PUBLIC_URL}/assets/images/global/mobile_logo.png`}
                alt="logo"
              />
            </Box>
            <Typography className="notice-title-2">
              <Trans i18nKey="popup:redeem_after_login_and_not_linked_with_sa" />{" "}
              <br /> <br />
              <LinearProgress
                variant="determinate"
                value={redirectToLinkUpProgress}
                className="custom-progress"
              />
            </Typography>

            <Typography className="notice-title-b ">
              <Trans i18nKey="popup:tutorial_video" />
            </Typography>
            <VideoPlayer
              videoUrl={
                isChinese
                  ? "https://hkclubs.samsung.com/Media/Video/link_acc_TC.mp4"
                  : "https://hkclubs.samsung.com/Media/Video/link_acc_EN.mp4"
              }
            />
          </Box>
        </Popup>
      )}
    </Box>
  );
};

export default RedeemSection;
